import React, { useState, useEffect, useRef, useMemo } from "react";
import { toast } from "react-hot-toast";
import { useHistory, useParams, useLocation } from "react-router-dom";
import {
  HiOutlineTrash,
  HiOutlineRefresh,
  HiOutlineChevronUp,
  HiOutlineChevronDown,
  HiOutlineX,
  HiOutlineDotsVertical,
  HiOutlineSearch,
  HiOutlineUserGroup,
  HiOutlinePencil,
  HiOutlineLockOpen,
} from "react-icons/hi";
import { PiClockCounterClockwise } from "react-icons/pi";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import API from "../../api";
import { capture } from "../../instrument";
import { INPUT_CLASS_NAME } from "../../utils";
import { useHotkeys } from "react-hotkeys-hook";
import useRecordNavShortcuts from "../../hooks/useRecordNavShortcuts";
import { SHORTCUTS } from "../../utils/shortcuts";
import { notifyShortcut } from "../../utils/shortcutTip";
import ClinicianFormModal from "../../components/clinic/ClinicianFormModal";

const EditCabinet = () => {
  const history = useHistory();
  const { id } = useParams();
  const location = useLocation();
  const queryClient = useQueryClient();

  const [clinicianSearch, setClinicianSearch] = useState("");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [editingClinician, setEditingClinician] = useState(null);
  const menuRef = useRef(null);
  const isDirtyRef = useRef(false);
  const markDirty = () => {
    isDirtyRef.current = true;
  };

  const [name, setName] = useState("");
  const [laboratory, setLaboratory] = useState("");
  const [status, setStatus] = useState("active");

  const filteredIds = location.state?.filteredIds || null;
  const currentIndex = useMemo(() => (filteredIds ? filteredIds.indexOf(id) : -1), [filteredIds, id]);
  const hasNavigation = filteredIds !== null && currentIndex !== -1;

  const preview = useMemo(() => {
    const cachedClinics = queryClient.getQueryData(["clinics"]);
    if (!cachedClinics) return null;
    const cached = cachedClinics.find((c) => c._id === id);
    return cached ? { name: cached.name, status: cached.status } : null;
  }, [id]);

  const navigateTo = (index) => {
    const targetId = filteredIds[index];
    history.replace({ pathname: `/clinic/${targetId}/edit`, state: { filteredIds } });
  };

  const navigatePrev = () => {
    if (!hasNavigation || currentIndex <= 0) return;
    if (isDirtyRef.current && !window.confirm("Vous avez des modifications non enregistrées. Voulez-vous vraiment quitter cette page ?")) return;
    navigateTo(currentIndex - 1);
  };

  const navigateNext = () => {
    if (!hasNavigation || currentIndex >= filteredIds.length - 1) return;
    if (isDirtyRef.current && !window.confirm("Vous avez des modifications non enregistrées. Voulez-vous vraiment quitter cette page ?")) return;
    navigateTo(currentIndex + 1);
  };

  const goBack = () => {
    if (isDirtyRef.current && !window.confirm("Vous avez des modifications non enregistrées. Voulez-vous vraiment quitter cette page ?")) return;
    history.push("/clinic");
  };

  useRecordNavShortcuts(
    {
      onPrev: navigatePrev,
      onNext: navigateNext,
      onBack: goBack,
      canPrev: hasNavigation && currentIndex > 0,
      canNext: hasNavigation && currentIndex < filteredIds?.length - 1,
      backEnabled: !isInviteModalOpen && !isMenuOpen && !editingClinician,
    },
    [hasNavigation, currentIndex, filteredIds, isInviteModalOpen, isMenuOpen, editingClinician],
  );

  const {
    data: clinicData,
    isLoading: isLoadingClinic,
    isError: isClinicError,
  } = useQuery({
    queryKey: ["clinic", id],
    queryFn: async () => {
      const response = await API.get(`/clinic/${id}`);
      if (!response.ok) throw new Error(response.message || "Impossible de charger la clinique");
      return response.data;
    },
    staleTime: Infinity,
  });

  const { data: laboratories = [], isError: isErrorLaboratories } = useQuery({
    queryKey: ["laboratories"],
    queryFn: async () => {
      const response = await API.get("/admin/laboratory");
      if (!response.ok) throw new Error(response.message);
      return response.data;
    },
  });

  useEffect(() => {
    if (!isErrorLaboratories) return;
    capture(new Error("Failed to load laboratories for clinic edit"));
    toast.error("Erreur lors du chargement des laboratoires");
  }, [isErrorLaboratories]);

  const { data: clinicians = [] } = useQuery({
    queryKey: ["clinicians", id],
    queryFn: async () => {
      const response = await API.get(`/clinician?clinic=${id}`);
      if (!response.ok) throw new Error(response.message);
      return response.data;
    },
  });

  useEffect(() => {
    if (!clinicData) return;
    setName(clinicData.name || "");
    setLaboratory(clinicData.laboratory || "");
    setStatus(clinicData.status || "active");
    isDirtyRef.current = false;
  }, [clinicData]);

  useEffect(() => {
    if (!isClinicError) return;
    toast.error("Erreur lors du chargement de la clinique");
    history.push("/clinic");
  }, [isClinicError]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const updateMutation = useMutation({
    mutationFn: (body) => API.put(`/clinic/${id}`, body),
    onSuccess: (response) => {
      if (!response.ok) return toast.error(response.message);
      isDirtyRef.current = false;
      toast.success("Clinique mis à jour avec succès");
      queryClient.invalidateQueries({ queryKey: ["clinics"] });
      queryClient.invalidateQueries({ queryKey: ["clinic", id] });
    },
    onError: (error) => {
      capture(error);
      toast.error("Erreur lors de la mise à jour de la clinique");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => API.delete(`/clinic/${id}`),
    onSuccess: (response) => {
      if (!response.ok) {
        if (response.code === "CLINIC_HAS_ACTIVE_CLINICIANS") {
          return toast.error("Impossible de supprimer une clinique avec des praticiens actifs");
        }
        return toast.error(response.message);
      }
      toast.success("Clinique supprimée avec succès");
      queryClient.invalidateQueries({ queryKey: ["clinics"] });
      history.push("/clinic");
    },
    onError: (error) => {
      capture(error);
      toast.error("Erreur lors de la suppression de la clinique");
    },
  });

  const inviteMutation = useMutation({
    mutationFn: (body) => API.post("/clinician/invite", body),
    onSuccess: (response) => {
      toast.success(response.data.email ? "Invitation envoyée" : "Praticien ajouté");
      setIsInviteModalOpen(false);
      queryClient.invalidateQueries({ queryKey: ["clinicians", id] });
      queryClient.invalidateQueries({ queryKey: ["clinician"] });
    },
    onError: (error) => {
      if (error?.code === "CLINICIAN_ALREADY_EXISTS") return toast.error("Ce praticien existe déjà");
      capture(error);
      toast.error("Erreur lors de l'envoi de l'invitation");
    },
  });

  const updateClinicianMutation = useMutation({
    mutationFn: ({ clinicianId, body }) => API.put(`/clinician/${clinicianId}`, body),
    onSuccess: () => {
      toast.success("Praticien mis à jour avec succès");
      setEditingClinician(null);
      queryClient.invalidateQueries({ queryKey: ["clinicians", id] });
      queryClient.invalidateQueries({ queryKey: ["clinician"] });
    },
    onError: (error) => {
      if (error?.code === "CLINICIAN_ALREADY_EXISTS") return toast.error("Ce praticien existe déjà");
      capture(error);
      toast.error("Erreur lors de la mise à jour du praticien");
    },
  });

  const deleteClinicianMutation = useMutation({
    mutationFn: (clinicianId) => API.delete(`/clinician/${clinicianId}`),
    onSuccess: (response) => {
      if (!response.ok) return toast.error(response.message);
      toast.success("Praticien supprimé avec succès");
      queryClient.invalidateQueries({ queryKey: ["clinicians", id] });
      queryClient.invalidateQueries({ queryKey: ["clinician"] });
    },
    onError: (error) => {
      capture(error);
      toast.error("Erreur lors de la suppression du praticien");
    },
  });

  const resendInviteMutation = useMutation({
    mutationFn: (clinicianId) => API.post(`/clinician/resend-invite/${clinicianId}`),
    onSuccess: (response) => {
      if (!response.ok) return toast.error(response.message);
      toast.success("Invitation renvoyée avec succès");
    },
    onError: (error) => {
      capture(error);
      toast.error("Erreur lors de l'envoi de l'invitation");
    },
  });

  const unblockOtpMutation = useMutation({
    mutationFn: (clinicianId) => API.put(`/clinician/${clinicianId}`, { isOtpDisabled: false }),
    onSuccess: () => {
      toast.success("Accès débloqué avec succès");
      queryClient.invalidateQueries({ queryKey: ["clinicians", id] });
    },
    onError: (error) => {
      capture(error);
      toast.error("Erreur lors du déblocage de l'accès");
    },
  });

  const isMutating =
    updateMutation.isPending ||
    deleteMutation.isPending ||
    inviteMutation.isPending ||
    deleteClinicianMutation.isPending ||
    resendInviteMutation.isPending ||
    unblockOtpMutation.isPending ||
    updateClinicianMutation.isPending;

  const updateClinic = () => {
    if (!name) return toast.error("Le nom de la clinique est requis");
    updateMutation.mutate({ name, laboratory, status });
  };

  useHotkeys(SHORTCUTS.EDIT_SAVE.keys, () => updateClinic(), { preventDefault: true, enableOnFormTags: true, enabled: !updateMutation.isPending }, [
    updateClinic,
    updateMutation.isPending,
  ]);

  const deleteClinic = () => {
    const confirmed = window.confirm("Voulez-vous vraiment supprimer cette clinique ? Cette action est irréversible.");
    if (!confirmed) return;
    deleteMutation.mutate();
  };

  const onInviteSubmit = (values) => inviteMutation.mutate({ ...values, clinic: id });

  const onSaveClinician = (values) => updateClinicianMutation.mutate({ clinicianId: editingClinician._id, body: values });

  const deleteClinician = (clinicianId) => {
    const confirmed = window.confirm("Voulez-vous vraiment supprimer ce praticien ?");
    if (!confirmed) return;
    deleteClinicianMutation.mutate(clinicianId);
  };

  const resendInvite = (clinicianId) => {
    resendInviteMutation.mutate(clinicianId);
  };

  const formatDate = (date) => {
    if (!date) return "Jamais";
    return new Date(date).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  const filteredClinicians = clinicians.filter((c) => {
    if (!clinicianSearch) return true;
    const searchLower = clinicianSearch.toLowerCase();
    const matchesEmail = c.email?.toLowerCase().includes(searchLower);
    const fullName = `${c.firstName || ""} ${c.lastName || ""}`.trim();
    const matchesName = fullName.toLowerCase().includes(searchLower);
    const matchesCode = c.code?.toLowerCase().includes(searchLower);
    return matchesEmail || matchesName || matchesCode;
  });

  return (
    <div className="flex h-[calc(100vh-60px)] flex-col bg-gray-50">
      {hasNavigation && (
        <div className="flex items-center gap-1.5 border-b border-gray-200 bg-white px-3 py-1.5">
          <button type="button" className="rounded-md p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-600" onClick={goBack} title="Fermer (Esc)">
            <HiOutlineX className="h-4 w-4" />
          </button>
          <div className="mx-0.5 h-4 w-px bg-gray-200" />
          <button
            type="button"
            className="rounded-md p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-gray-500"
            onClick={navigatePrev}
            disabled={currentIndex <= 0}
          >
            <HiOutlineChevronUp className="h-4 w-4" />
          </button>
          <button
            type="button"
            className="rounded-md p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-gray-500"
            onClick={navigateNext}
            disabled={currentIndex >= filteredIds.length - 1}
          >
            <HiOutlineChevronDown className="h-4 w-4" />
          </button>
          <span className="ml-1 text-xs tabular-nums text-gray-500">
            {currentIndex + 1} / {filteredIds.length}
          </span>
        </div>
      )}

      <div className="flex items-start justify-between border-b border-gray-200 bg-white px-8 pb-5 pt-6">
        <div className="min-w-0">
          <div className="flex items-center gap-3">
            <h1 className="truncate text-lg font-semibold text-gray-900">{clinicData?.name || preview?.name || "Clinique"}</h1>
            {(clinicData?.status || preview?.status) && (
              <span className="flex shrink-0 items-center gap-1.5">
                <span className={`h-1.5 w-1.5 rounded-full ${(clinicData?.status || preview?.status) === "active" ? "bg-green-500" : "bg-gray-300"}`} />
                <span className={`text-xs ${(clinicData?.status || preview?.status) === "active" ? "text-gray-600" : "text-gray-400"}`}>
                  {(clinicData?.status || preview?.status) === "active" ? "Actif" : "Inactif"}
                </span>
              </span>
            )}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1.5 pl-4">
          <div className="relative" ref={menuRef}>
            <button type="button" className="rounded-md p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-600" onClick={() => setIsMenuOpen(!isMenuOpen)}>
              <HiOutlineDotsVertical className="h-5 w-5" />
            </button>
            {isMenuOpen && (
              <div className="absolute right-0 z-10 mt-1 w-48 rounded-lg border border-gray-200 bg-white shadow-lg">
                <button
                  type="button"
                  className="flex w-full items-center gap-2 rounded-t-lg px-3 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50"
                  onClick={() => {
                    setIsMenuOpen(false);
                    window.open(`/audit-log/clinics/${id}`, "_blank", "noopener,noreferrer,width=1000,height=800");
                  }}
                >
                  <PiClockCounterClockwise className="h-4 w-4" />
                  Historique
                </button>
                <button
                  type="button"
                  className="flex w-full items-center gap-2 rounded-b-lg px-3 py-2 text-sm text-red-600 transition-colors hover:bg-red-50"
                  onClick={() => {
                    setIsMenuOpen(false);
                    deleteClinic();
                  }}
                  disabled={isMutating}
                >
                  <HiOutlineTrash className="h-4 w-4" />
                  Supprimer
                </button>
              </div>
            )}
          </div>
          <button
            type="button"
            className="rounded-lg bg-primary-500 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50"
            onClick={() => {
              updateClinic();
              notifyShortcut("EDIT_SAVE");
            }}
            disabled={isMutating || isLoadingClinic}
          >
            {updateMutation.isPending ? "Enregistrement..." : "Enregistrer"}
          </button>
        </div>
      </div>

      {isLoadingClinic ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-primary-500" />
          <span className="text-sm text-gray-400">Chargement...</span>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto" onChangeCapture={markDirty}>
          <div className="mx-auto max-w-4xl px-8 py-6">
            <h3 className="mb-4 text-lg font-medium text-gray-900">Général</h3>

            <div className="divide-y divide-gray-100 rounded-lg border border-gray-100 bg-white">
              <div className="flex items-center gap-4 px-5 py-3.5">
                <label className="w-44 shrink-0 text-[13px] font-medium text-gray-600">Nom de la clinique</label>
                <input className={INPUT_CLASS_NAME} placeholder="Nom de la clinique" value={name} onChange={(e) => setName(e.target.value)} disabled={isMutating} />
              </div>
              <div className="flex items-center gap-4 px-5 py-3.5">
                <label className="w-44 shrink-0 text-[13px] font-medium text-gray-600">Laboratoire</label>
                <select className={INPUT_CLASS_NAME} value={laboratory} onChange={(e) => setLaboratory(e.target.value)} disabled={isMutating}>
                  <option value="">Sélectionner un laboratoire</option>
                  {laboratories.map((lab) => (
                    <option key={lab._id} value={lab._id}>
                      {lab.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <h3 className="mb-4 mt-8 text-lg font-medium text-gray-900">Praticiens</h3>

            <div className="overflow-hidden rounded-lg border border-gray-100 bg-white">
              <div className="flex items-center gap-3 border-b border-gray-100 px-4 py-2.5">
                <div className="relative flex-1">
                  <HiOutlineSearch className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Rechercher un praticien..."
                    value={clinicianSearch}
                    onChange={(e) => setClinicianSearch(e.target.value)}
                    className="w-full bg-transparent py-1 pl-8 pr-3 text-sm outline-none placeholder:text-gray-400"
                  />
                </div>
                <button
                  type="button"
                  className="rounded-lg bg-primary-500 px-3 py-1.5 text-sm text-white transition-colors hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50"
                  onClick={() => setIsInviteModalOpen(true)}
                  disabled={isMutating}
                >
                  Inviter
                </button>
              </div>

              <div className="max-h-80 divide-y divide-gray-100 overflow-y-auto">
                {filteredClinicians.length === 0 && clinicians.length > 0 && (
                  <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                    <p className="text-sm">{`Aucun résultat pour "${clinicianSearch}"`}</p>
                  </div>
                )}

                {filteredClinicians.length === 0 && clinicians.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                    <HiOutlineUserGroup className="mb-3 h-8 w-8 text-gray-300" />
                    <p className="text-sm">Aucun praticien dans cette clinique</p>
                    <p className="mt-1 text-xs">Invitez des praticiens pour commencer</p>
                  </div>
                )}

                {filteredClinicians.map((c) => {
                  const fullName = c.firstName || c.lastName ? `${c.firstName || ""} ${c.lastName || ""}`.trim() : null;
                  return (
                    <div key={c._id} className="group flex items-center px-4 py-3 transition-colors hover:bg-gray-50">
                      <span className="min-w-0 truncate text-sm font-medium text-gray-900">{c.email || fullName}</span>
                      {c.isOtpDisabled && (
                        <span className="ml-2 inline-flex shrink-0 items-center rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-600">Bloqué</span>
                      )}

                      <div className="ml-auto flex shrink-0 items-center gap-4 pl-4">
                        {c.email && fullName && <span className="hidden max-w-[140px] truncate text-xs text-gray-500 sm:block">{fullName}</span>}
                        {c.code && <span className="hidden text-xs tabular-nums text-gray-400 sm:block">{c.code}</span>}
                        <span className="hidden text-xs tabular-nums text-gray-400 md:block">{formatDate(c.last_login_at)}</span>
                        <div className="flex items-center gap-1">
                          {c.isOtpDisabled && (
                            <button
                              type="button"
                              onClick={() => unblockOtpMutation.mutate(c._id)}
                              className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-green-50 hover:text-green-600"
                              title="Débloquer l'accès"
                            >
                              <HiOutlineLockOpen className="h-4 w-4" />
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => setEditingClinician(c)}
                            className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-primary-50 hover:text-primary-500"
                            title="Modifier le praticien"
                          >
                            <HiOutlinePencil className="h-4 w-4" />
                          </button>
                          {c.email && (
                            <button
                              type="button"
                              onClick={() => resendInvite(c._id)}
                              className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-primary-50 hover:text-primary-500"
                              title="Renvoyer l'invitation"
                            >
                              <HiOutlineRefresh className="h-4 w-4" />
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => deleteClinician(c._id)}
                            className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-500"
                            title="Supprimer le praticien"
                          >
                            <HiOutlineTrash className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <h3 className="mb-4 mt-8 text-lg font-medium text-gray-900">Accès</h3>

            <div className="divide-y divide-gray-100 rounded-lg border border-gray-100 bg-white">
              <div className="flex items-center justify-between gap-4 px-5 py-3.5">
                <div className="shrink-0">
                  <span className="text-[13px] font-medium text-gray-600">Activer la clinique</span>
                  <span className="block text-xs text-gray-500">Les praticiens pourront plus envoyer des demandes si la clinique est désactivée</span>
                </div>
                <button
                  type="button"
                  className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${status === "active" ? "bg-primary-500" : "bg-gray-300"}`}
                  onClick={() => {
                    markDirty();
                    setStatus(status === "active" ? "inactive" : "active");
                  }}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${status === "active" ? "translate-x-5" : "translate-x-0"}`}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isInviteModalOpen && (
        <ClinicianFormModal
          title="Inviter un praticien"
          submitLabel="Envoyer l'invitation"
          isPending={inviteMutation.isPending}
          onSubmit={onInviteSubmit}
          onClose={() => setIsInviteModalOpen(false)}
        />
      )}

      {editingClinician && (
        <ClinicianFormModal
          title="Modifier le praticien"
          submitLabel="Enregistrer"
          initialValues={editingClinician}
          isPending={updateClinicianMutation.isPending}
          onSubmit={onSaveClinician}
          onClose={() => setEditingClinician(null)}
        />
      )}
    </div>
  );
};

export default EditCabinet;
